import moment from "moment";

const participantFields = [
  "id",
  "name",
  "address",
  "email",
  "mobilePhone",
  "documentId",
  "contactPerson",
  "contactPhone",
  "bankAccount",
  "postalCode",
  "city",
  "country",
  "cityCode",
  "save",
];

const extractNewParticipantName = (participantDetails) => {
  const { label } = participantDetails;
  if (label && label.includes("Nuevo")) {
    let splitIndex = label.indexOf("(") - 1;
    let transformedName = label?.slice(0, splitIndex);
    return transformedName;
  }
  return participantDetails?.label;
};

const formatPreparationDate = (date) => {
  if (!date) return undefined;

  return moment(date)
    .seconds(0)
    .milliseconds(0)
    .utc()
    .format("YYYY-MM-DDTHH:mm:ss.SSSZ");
};

const extractParticipantDetails = (preRegistrationDetails, participantType) => {
  const participant = {
    participantId: preRegistrationDetails[`${participantType}ParticipantId`],
    name: preRegistrationDetails[`${participantType}Name`]?.label
      ? extractNewParticipantName(
          preRegistrationDetails[`${participantType}Name`]
        )
      : preRegistrationDetails[`${participantType}Name`],
    address: preRegistrationDetails[`${participantType}Address`],
    email: preRegistrationDetails[`${participantType}Email`],
    mobilePhone: preRegistrationDetails[`${participantType}MobilePhone`],
    documentId: preRegistrationDetails[`${participantType}DocumentId`],
    contactPerson: preRegistrationDetails[`${participantType}ContactPerson`],
    contactPhone: preRegistrationDetails[`${participantType}ContactPhone`],
    bankAccount:
      preRegistrationDetails[`${participantType}BankAccount`]?.trim(),
    postalCode: preRegistrationDetails[`${participantType}PostalCode`],
    city: preRegistrationDetails[`${participantType}City`],
    country: preRegistrationDetails[`${participantType}Country`],
    cityCode: preRegistrationDetails[`${participantType}CityCode`],
    save: preRegistrationDetails[`${participantType}Save`],
  };

  return participant;
};

export const modelPreregistrationData = (preRegistrationDetails) => {
  const isProximity = preRegistrationDetails?.serviceType === "Frio Proximidad";
  const isMassive = preRegistrationDetails?.massivePreRegistration;
  const isColdchain = preRegistrationDetails?.coldchain;
  const isPaqFarma =
    preRegistrationDetails?.productType?.key?.includes("FRAXS");

  const commonFields = {
    contractNumber: preRegistrationDetails?.contractNumber,
    clientNumber: preRegistrationDetails?.clientNumber,
    labellerCode: preRegistrationDetails?.labellerCode,
    sender: extractParticipantDetails(preRegistrationDetails, "sender"),
  };

  const clean = (obj) => {
    return Object.keys(obj).reduce((acc, key) => {
      if (obj[key] !== undefined && obj[key] !== null) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});
  };

  const buildProximityData = () => {
    const product = preRegistrationDetails.sameDayDeliveryConfirmation
      ? "PAAZF"
      : "PADXA";
    const deliveryMethod = "DOUAOF";

    if (isMassive) {
      return clean({
        product,
        deliveryMethod,
        ...commonFields,
        file: preRegistrationDetails?.file,
      });
    }

    const addressee = extractParticipantDetails(
      preRegistrationDetails,
      "recipient"
    );

    const packageData = {
      clientReference: preRegistrationDetails?.clientReference,
      observations: preRegistrationDetails?.observations,
      tariffNumber: preRegistrationDetails?.tariffNumber,
      netValue: preRegistrationDetails?.netValue,
      netWeight: preRegistrationDetails?.netWeight,
      quantity: preRegistrationDetails?.quantity,
      description: preRegistrationDetails?.description,
    };

    const total = preRegistrationDetails?.packagesTotal?.key || 1;
    const packages = Array.from({ length: total }, () => ({ ...packageData }));

    return clean({
      product,
      deliveryMethod,
      ...commonFields,
      addressee,
      packages,
      electronicDelivery: preRegistrationDetails?.electronicDelivery,
      addresseeExclusiveDelivery:
        preRegistrationDetails?.addresseeExclusiveDelivery,
      declaredValue: preRegistrationDetails?.declaredValue,
      declaredValueAmount: preRegistrationDetails?.declaredValueAmount,
      paymentOnDelivery: preRegistrationDetails?.paymentOnDelivery,
      paymentOnDeliveryAmount: preRegistrationDetails?.paymentOnDeliveryAmount,
    });
  };

  const buildRegularServiceData = (isColdchain) => {
    const [product, deliveryMethod] =
      preRegistrationDetails?.productType?.key?.split("-") || [];

    if (isMassive) {
      return clean({
        product,
        deliveryMethod,
        ...commonFields,
        file: preRegistrationDetails?.file,
        specialDestination: preRegistrationDetails?.specialDestination,
      });
    }

    const addressee = extractParticipantDetails(
      preRegistrationDetails,
      "recipient"
    );
    const temperatureRangeID = preRegistrationDetails?.temperatureRangeID?.key;
    const temperatureTraceabilityID =
      preRegistrationDetails?.temperatureTraceabilityID?.key;

    const packages = [
      {
        width: preRegistrationDetails?.width,
        height: preRegistrationDetails?.height,
        length: preRegistrationDetails?.length,
        weight: preRegistrationDetails?.weight,
        clientReference: preRegistrationDetails?.clientReference,
        observations: preRegistrationDetails?.observations,
        temperatureRangeID,
        tariffNumber: preRegistrationDetails?.tariffNumber,
        netValue: preRegistrationDetails?.netValue,
        netWeight: preRegistrationDetails?.netWeight,
        quantity: preRegistrationDetails?.quantity,
        description: preRegistrationDetails?.description,
      },
    ];

    const base = {
      product,
      deliveryMethod,
      ...commonFields,
      addressee,
      imei: preRegistrationDetails.imei,
      temperatureTraceabilityID,
      dataLoggerID: preRegistrationDetails?.dataLoggerID,
      packages,
      electronicDelivery: preRegistrationDetails?.electronicDelivery,
      addresseeExclusiveDelivery:
        preRegistrationDetails?.addresseeExclusiveDelivery,
      declaredValue: preRegistrationDetails?.declaredValue,
      declaredValueAmount: preRegistrationDetails?.declaredValueAmount,
      paymentOnDelivery: preRegistrationDetails?.paymentOnDelivery,
      paymentOnDeliveryAmount: preRegistrationDetails?.paymentOnDeliveryAmount,
    };

    if (preRegistrationDetails?.paymentOnDelivery) {
      base.addressee.bankAccount = base.sender?.bankAccount;
    }

    if (isPaqFarma) {
      base.preparationDate = formatPreparationDate(
        preRegistrationDetails?.preparationDate
      );
    }

    return clean(base);
  };

  const modelledData = isProximity
    ? buildProximityData()
    : buildRegularServiceData(isColdchain);

  return modelledData;
};

export function extractMassiveErrors(data) {
  return data
    .filter((item) => Array.isArray(item.errors) && item.errors.length > 0)
    .flatMap((item) =>
      item.errors.map((error) => `Línea ${item.line}: ${error}`)
    );
}

export function splitErrorMessages(errors) {
  return errors.flatMap((errorMsg) =>
    errorMsg
      .split("|||")
      .map((e) => e.trim())
      .filter((e) => e !== "")
  );
}
